import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	IncentiveRow,
	DayTotals,
	IncentiveReportSummary,
} from "../models/IncentiveRow";
import type { IncentiveShiftOverride } from "../models/IncentiveShiftOverride";
import type {
	CrewPairSummary,
	CrewPivotRow,
	CrewWeeklySummary,
	RotationMeta,
	TankData,
	TankRawData,
	WeeklyIncentiveData,
} from "../models/WeeklyIncentiveData";

/** Day abbreviations for date formatting. */
const SHORT_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

/** Formats a ProductionDate string (e.g. "2026-05-26") as "Mon 05/26". */
const formatProdDate = (isoDate: string): string => {
	// Parse as local date parts to avoid timezone shift
	const [y, m, d] = isoDate.substring(0, 10).split("-").map(Number);
	const dt = new Date(y, m - 1, d);
	const dayName = SHORT_DAYS[dt.getDay()];
	const mm = String(m).padStart(2, "0");
	const dd = String(d).padStart(2, "0");
	return `${dayName} ${mm}/${dd}`;
};

/** RS7 raw row shape from the SP. */
interface RS7RawRow {
	RowType: string;
	BlockId: number | null;
	ProductionDate: string | null;
	CrewA: number | null;
	CrewB: number | null;
	CrewC: number | null;
	CrewD: number | null;
	TotalAB: number | null;
	TotalCD: number | null;
	IsFutureDay: boolean | null;
	CurrentBlockStart: string | null;
	CurrentBlockEnd: string | null;
	CurrentCrewPair: string | null;
	PrevBlockStart: string | null;
	PrevBlockEnd: string | null;
	PrevCrewPair: string | null;
	// Raw finished/target per crew pair for weighted total computation
	FinishedAB: number | null;
	TargetAB: number | null;
	FinishedCD: number | null;
	TargetCD: number | null;
}

/** Builds rotation-based crew pivot from RS7 META + DATA rows. */
const buildCrewPivotFromRotation = (
	rs7Rows: RS7RawRow[],
): { meta: RotationMeta; pivot: CrewPivotRow[] } | null => {
	if (!rs7Rows || rs7Rows.length === 0) return null;

	const metaRow = rs7Rows.find((r) => r.RowType === "META");
	if (!metaRow || !metaRow.CurrentBlockStart || !metaRow.CurrentCrewPair)
		return null;

	const meta: RotationMeta = {
		currentBlockStart: metaRow.CurrentBlockStart,
		currentBlockEnd: metaRow.CurrentBlockEnd!,
		currentCrewPair: metaRow.CurrentCrewPair as "AB" | "CD",
		prevBlockStart: metaRow.PrevBlockStart ?? "",
		prevBlockEnd: metaRow.PrevBlockEnd ?? "",
		prevCrewPair: (metaRow.PrevCrewPair as "AB" | "CD") ?? "CD",
	};

	const dataRows = rs7Rows.filter(
		(r) => r.RowType === "DATA" && r.ProductionDate != null,
	);
	if (dataRows.length === 0) return null;

	// Day rows: SP already sends null (no shift) vs 0 (shift, no production).
	// Frontend just marks future days so the pill renderer shows gray.
	const pivotRows: CrewPivotRow[] = dataRows.map((r) => {
		const isFuture = !!r.IsFutureDay;
		const futureKeys = new Set<string>();
		if (isFuture) {
			if (r.CrewA != null) futureKeys.add("crewA");
			if (r.CrewB != null) futureKeys.add("crewB");
			if (r.CrewC != null) futureKeys.add("crewC");
			if (r.CrewD != null) futureKeys.add("crewD");
			if (r.TotalAB != null) futureKeys.add("totalAB");
			if (r.TotalCD != null) futureKeys.add("totalCD");
		}

		return {
			date: formatProdDate(r.ProductionDate!),
			crewA: r.CrewA,
			crewB: r.CrewB,
			crewC: r.CrewC,
			crewD: r.CrewD,
			totalAB: r.TotalAB,
			totalCD: r.TotalCD,
			isTotal: false,
			futureKeys,
			blockId: r.BlockId ?? undefined,
		};
	});

	// Total row: SUM(finished) / SUM(target) per pair.
	// target=0 → null → "—" gray pill (can't divide by zero).
	const pairTotals = { AB: { fin: 0, tgt: 0 }, CD: { fin: 0, tgt: 0 } };
	for (const r of dataRows) {
		pairTotals.AB.tgt += r.TargetAB ?? 0;
		pairTotals.CD.tgt += r.TargetCD ?? 0;
		if (!r.IsFutureDay) {
			pairTotals.AB.fin += r.FinishedAB ?? 0;
			pairTotals.CD.fin += r.FinishedCD ?? 0;
		}
	}

	const pct = (fin: number, tgt: number) =>
		tgt > 0 ? (fin / tgt) * 100 : null;

	pivotRows.push({
		date: "Total",
		crewA: null,
		crewB: null,
		crewC: null,
		crewD: null,
		totalAB: pct(pairTotals.AB.fin, pairTotals.AB.tgt),
		totalCD: pct(pairTotals.CD.fin, pairTotals.CD.tgt),
		isTotal: true,
		futureKeys: new Set<string>(),
	});

	return { meta, pivot: pivotRows };
};

/**
 * Returns weekly incentive data from [M2].[GetIncentiveByWeek].
 * RS1: shift rows, RS2: day totals, RS3: crew weekly, RS4: crew pairs, RS5: tanks raw.
 * Frontend builds: pivot table + tank labels.
 */
export const getWeeklyIncentive = async (
	weekEndDate: string,
	signal?: AbortSignal,
): Promise<WeeklyIncentiveData> => {
	const moment = getMomentTz();
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "WeekEndingDate", value: weekEndDate },
	];
	const resp = await apiService.callV2(
		"[M2].[GetIncentiveByWeek]",
		parameters,
		signal,
	);
	const tables = get(resp, "data.tables", {});

	const dailyRows = (get(tables, "0.rows", []) as any[]).map((r) => ({
		...r,
		LastOverrideAt: r.LastOverrideAt
			? moment.utc(r.LastOverrideAt).toDate()
			: null,
		LastAdjustmentAt: r.LastAdjustmentAt
			? moment.utc(r.LastAdjustmentAt).toDate()
			: null,
	})) as IncentiveRow[];

	const dayTotals = get(tables, "1.rows", []) as DayTotals[];
	const crewWeekly = get(tables, "2.rows", []) as CrewWeeklySummary[];
	const crewPairs = get(tables, "3.rows", []) as CrewPairSummary[];
	const tanksRaw = get(tables, "4.rows", []) as TankRawData[];

	// Build tank presentation data from raw SP response
	/** Formats ProductionDate (DATE from SP) as MM/dd without timezone shift */
	const fmtDate = (v: string | null): string => {
		if (!v) return "";
		const s = String(v).substring(0, 10); // "2026-05-28"
		const [, m, d] = s.split("-");
		return `${m}/${d}`;
	};

	const tanks: TankData[] = tanksRaw.map((t) => {
		let label = "";
		let sublabel = "";
		const shiftLabel = t.Shift ?? "";

		switch (t.TankKey) {
			case "current": {
				label = "Current Shift";
				if (t.ProductionDate && t.Crew) {
					sublabel = `${fmtDate(t.ProductionDate)} ${shiftLabel} — Crew ${t.Crew}`;
				}
				break;
			}
			case "last": {
				label = "Last Shift";
				if (t.ProductionDate && t.Crew) {
					sublabel = `${fmtDate(t.ProductionDate)} ${shiftLabel} — Crew ${t.Crew}`;
				}
				break;
			}
			case "weekAB": {
				label = "Week A/B";
				sublabel = "Weekly total";
				break;
			}
			case "weekCD": {
				label = "Week C/D";
				sublabel = "Weekly total";
				break;
			}
		}

		return {
			tankKey: t.TankKey,
			label,
			sublabel,
			finished: t.Finished ?? 0,
			target: t.Target ?? 0,
			incPercent: t.IncPercent ?? null,
		};
	});

	// Parse RS7 (rotation-based crew pivot) — index 6
	const rs7Rows = get(tables, "6.rows", []) as RS7RawRow[];
	const rotationResult = buildCrewPivotFromRotation(rs7Rows);

	const crewPivot = rotationResult?.pivot ?? [];
	const rotationMeta = rotationResult?.meta ?? null;

	return {
		dailyRows,
		dayTotals,
		crewWeekly,
		crewPairs,
		tanks,
		crewPivot,
		lastUpdated: (() => {
			const meta = get(tables, "5.rows.0", {}) as {
				LastModified?: string | null;
			};
			return meta.LastModified ? moment.utc(meta.LastModified).toDate() : null;
		})(),
		rotationMeta,
	};
};

/**
 * Returns incentive report data for a date range + optional crew.
 * RS1: shift-level rows, RS2: summary totals (filtered by FinishedTons > 0 in SP).
 */
export const getIncentiveReport = async (
	startDate: string,
	endDate: string,
	crews?: string[],
	shift?: string | null,
	signal?: AbortSignal,
): Promise<{ rows: IncentiveRow[]; summary: IncentiveReportSummary }> => {
	const moment = getMomentTz();
	const apiService = new MESApiService();
	const crewParam = crews && crews.length > 0 ? crews.join(",") : null;
	const parameters: spParameter[] = [
		{ name: "StartDate", value: startDate },
		{ name: "EndDate", value: endDate },
		...(crewParam ? [{ name: "Crew", value: crewParam }] : []),
		...(shift ? [{ name: "Shift", value: shift }] : []),
	];
	const resp = await apiService.callV2(
		"[M2].[GetIncentiveReport]",
		parameters,
		signal,
	);
	const tables = get(resp, "data.tables", {});

	const rows = (get(tables, "0.rows", []) as any[]).map((r) => ({
		...r,
		LastOverrideAt: r.LastOverrideAt
			? moment.utc(r.LastOverrideAt).toDate()
			: null,
		LastAdjustmentAt: r.LastAdjustmentAt
			? moment.utc(r.LastAdjustmentAt).toDate()
			: null,
	})) as IncentiveRow[];

	const summary = get(tables, "1.rows.0", {}) as IncentiveReportSummary;

	return { rows, summary };
};

/**
 * Saves a shift override (target tons, adjustment, notes).
 * Calls [M2].[UpsertIncentiveOverride].
 */
export const updateShiftOverride = async (
	payload: IncentiveShiftOverride,
): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "ShiftId", value: payload.ShiftId },
		{ name: "TargetTonsOverride", value: payload.TargetTonsOverride },
		{ name: "Adjustment", value: payload.Adjustment },
		{ name: "Notes", value: payload.Notes },
	];
	const resp = await apiService.callV2(
		"[M2].[UpsertIncentiveOverride]",
		parameters,
	);
	if (!resp.ok) throw new Error(resp.message || "Error saving override");
};
